// @overview: Utilities over DOM API
/**
 * @private
 */
export function changeBodyClass(className: string, type: 'add' | 'remove') {
  if (document.body) {
    if (type === 'add') {
      document.body.className += ` ${className}`;
    } else {
      document.body.className = document.body.className
        .split(' ')
        .filter((cn) => cn !== className)
        .join(' ');
    }
  }
}

const userAgentPatterns = ['MSIE ', 'Trident/', 'Edge/'];

/**
 * Check if current browser is made by Microsoft, which usually
 * has compatibility issues and requires special handling
 * @param userAgent default to `window.navigator.userAgent`
 */
export function isMicrosoftBrowser(userAgent = window.navigator.userAgent) {
  return new RegExp(userAgentPatterns.join('|')).test(userAgent);
}

export interface IScrollToOptions {
  offset?: number;
  behavior?: ScrollOptions['behavior'];
}

function legacyScrollTo(offsetPosition: number) {
  window.scrollTo(0, offsetPosition);
}

/**
 * Scroll window to specific target.
 *
 * See it in action [here](#just-like-native-button).
 *
 * @param targetEl target of the scroll
 * @param scrollOptions similar option for `window.scrollTo`
 * @param scrollOptions.offset the offset between the target element and the top of the window. Default to `160` (the height of site header + `<flos-pricebar />`).
 * @param scrollOptions.behavior the scrolling behavior. Default to 'smooth'. This parameter will be ignored in IE/Microsoft Edge because they doesn't support the smooth scrolling behavior by default.
 */
export function scrollTo(targetEl: HTMLElement, scrollOptions: IScrollToOptions = {}) {
  if (process.env.NODE_ENV === 'test' && !process.env.__MIGRATION_FLOS_TEST__) {
    return;
  }

  const { offset = 160, behavior = 'smooth' } = scrollOptions;

  try {
    const bodyRect = document.body.getBoundingClientRect().top;
    const targetRect = targetEl.getBoundingClientRect().top;
    const targetPosition = targetRect - bodyRect;
    const offsetPosition = targetPosition - offset;
    if (isMicrosoftBrowser()) {
      legacyScrollTo(offsetPosition);
    } else {
      try {
        window.scrollTo({
          behavior,
          top: offsetPosition,
        });
      } catch (e) {
        // old browser may throw error if they doesn't support, we will attempt to use the legacy ver instead
        legacyScrollTo(offsetPosition);
      }
    }
  } catch (err) {
    // scrollTo should not throw error and break your application
    console.error(err);
    console.error('scrollTo not supported');
  }
}

/**
 * Preload image eagerly so it will render instantly when you need it.
 *
 * Beware that doing this excessively wastes your user bandwidth unnecessarily.
 * @param imageSrc url for the image that you want to preload
 * @param onLoad callback to invoked when the image is loaded successfully
 */
export function preloadImage(imageSrc: string, onLoad?: () => void) {
  const image = new Image();
  if (onLoad) {
    image.onload = onLoad;
  }
  image.src = imageSrc;
}

/**
 * Copy string to clipboard so easy do not need to manually do it.
 *
 * See example [here](#copy-transient-demo).
 *
 * @param textToCopy string to be copied to clipboard
 */
export function copyToClipboard(textToCopy: string): void {
  const currentActive = document.activeElement as HTMLElement;

  const textarea = document.createElement('textarea');
  textarea.value = textToCopy;
  document.body.appendChild(textarea);

  textarea.select();
  document.execCommand('copy');

  document.body.removeChild(textarea);

  if (currentActive && currentActive !== document.body && currentActive.focus) {
    currentActive.focus();
  }
}
